import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { CalendarRange, Lock, Unlock, Plus, ShieldCheck } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return iso;
  }
};

const STATUS_PILL = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed: "bg-slate-200 text-slate-700 border-slate-300",
};

function deriveNextFY() {
  // Default to next April–March
  const today = new Date();
  const y = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const start = `${y}-04-01`;
  const end = `${y + 1}-03-31`;
  const label = `FY ${y}-${String((y + 1) % 100).padStart(2, "0")}`;
  return { label, start_date: start, end_date: end };
}

export default function FinancialYears() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(deriveNextFY());
  const [busy, setBusy] = useState(false);

  const [confirmDlg, setConfirmDlg] = useState(null);
  // { type: "close" | "reopen", fy: {...} }

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: act }] = await Promise.all([
        api.get("/financial-years"),
        api.get("/financial-years/active"),
      ]);
      setRows(list || []);
      setActive(act);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const submitCreate = async () => {
    if (!form.label || !form.start_date || !form.end_date) {
      toast.error("Label, start date and end date are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/financial-years", form);
      toast.success(`Created ${form.label}`);
      setShowCreate(false);
      setForm(deriveNextFY());
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const submitConfirm = async () => {
    if (!confirmDlg) return;
    setBusy(true);
    try {
      const path = confirmDlg.type === "close"
        ? `/financial-years/${confirmDlg.fy.id}/close`
        : `/financial-years/${confirmDlg.fy.id}/reopen`;
      await api.post(path);
      toast.success(confirmDlg.type === "close" ? "FY closed" : "FY reopened");
      setConfirmDlg(null);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="financial-years-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <CalendarRange className="h-3 w-3" /> Master &amp; Lock
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Chivo" }}>
            Financial Years
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Manage Indian-FY (April–March) periods. Closing an FY locks payroll re-compute &amp;
            advance disbursement for that period.
          </p>
        </div>
        {isSuper && (
          <Button
            onClick={() => { setForm(deriveNextFY()); setShowCreate(true); }}
            className="brand-bg brand-bg-hover text-white"
            data-testid="create-fy-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Create Financial Year
          </Button>
        )}
      </div>

      {/* Active FY callout */}
      {active && (
        <Card className="border-emerald-200 bg-emerald-50/40" data-testid="active-fy-card">
          <CardContent className="p-4 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">
                Current FY (auto-derived if not explicitly created)
              </div>
              <div className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo" }}>
                {active.label}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                {fmtDate(active.start_date)} → {fmtDate(active.end_date)}
                {active.auto_derived && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                    Auto-derived · create explicit row to manage
                  </span>
                )}
              </div>
            </div>
            <span
              className={`text-[10px] uppercase font-semibold px-2 py-1 rounded border ${STATUS_PILL[active.status] || ""}`}
              data-testid="active-fy-status"
            >
              {active.status}
            </span>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2 text-left">Label</th>
                  <th className="px-3 py-2 text-left">Start</th>
                  <th className="px-3 py-2 text-left">End</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Created</th>
                  <th className="px-3 py-2 text-left">Closed</th>
                  {isSuper && <th className="px-3 py-2 text-right">Action</th>}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={isSuper ? 7 : 6} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={isSuper ? 7 : 6} className="px-3 py-8 text-center text-slate-400">
                    No financial years created yet. The current FY will be auto-derived for reports.
                  </td></tr>
                )}
                {rows.map((fy) => (
                  <tr key={fy.id} className="border-t border-slate-100" data-testid={`fy-row-${fy.label.replace(/\s+/g, "-")}`}>
                    <td className="px-3 py-2 font-medium text-slate-900">{fy.label}</td>
                    <td className="px-3 py-2 text-slate-700">{fmtDate(fy.start_date)}</td>
                    <td className="px-3 py-2 text-slate-700">{fmtDate(fy.end_date)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded border ${STATUS_PILL[fy.status] || ""}`}>
                        {fy.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {fy.created_by_name || "—"}
                      <br />
                      <span className="text-[10px] text-slate-400">{(fy.created_at || "").slice(0, 10)}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">
                      {fy.status === "closed" ? (
                        <>
                          {fy.closed_by_name || "—"}
                          <br />
                          <span className="text-[10px] text-slate-400">{(fy.closed_at || "").slice(0, 10)}</span>
                        </>
                      ) : "—"}
                    </td>
                    {isSuper && (
                      <td className="px-3 py-2 text-right">
                        {fy.status === "active" ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                            onClick={() => setConfirmDlg({ type: "close", fy })}
                            data-testid={`close-fy-${fy.id}`}
                          >
                            <Lock className="h-3 w-3 mr-1" /> Close
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                            onClick={() => setConfirmDlg({ type: "reopen", fy })}
                            data-testid={`reopen-fy-${fy.id}`}
                          >
                            <Unlock className="h-3 w-3 mr-1" /> Re-open
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Help */}
      <Card className="border-blue-200 bg-blue-50/40">
        <CardContent className="p-4 text-xs text-slate-700 leading-relaxed flex gap-3 items-start">
          <ShieldCheck className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />
          <div>
            <b>What happens when an FY is closed?</b>
            <ul className="list-disc ml-5 mt-1 space-y-0.5">
              <li>Payroll re-compute for any month in that FY is blocked (HTTP 423 Locked).</li>
              <li>Advance disbursement attempts in that closed period are blocked.</li>
              <li>Existing payslips &amp; vouchers remain viewable / downloadable.</li>
              <li>Only a Super Admin can re-open the FY.</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="create-fy-dialog">
          <DialogHeader>
            <DialogTitle>Create Financial Year</DialogTitle>
            <DialogDescription className="sr-only">
              Define a new Indian Financial Year with label, start date and end date. Overlapping ranges are not allowed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-slate-500">Label *</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. FY 2026-27"
                className="mt-1"
                data-testid="fy-label-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">Start Date *</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  className="mt-1"
                  data-testid="fy-start-input"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-slate-500">End Date *</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                  className="mt-1"
                  data-testid="fy-end-input"
                />
              </div>
            </div>
            <div className="text-[10px] text-slate-500">
              Indian Financial Year is typically <b>1-April → 31-March</b>. Overlapping periods are not allowed.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={submitCreate}
              disabled={busy}
              className="brand-bg brand-bg-hover text-white"
              data-testid="confirm-create-fy-btn"
            >
              {busy ? "Creating…" : "Create FY"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Close / Reopen Dialog */}
      <Dialog open={!!confirmDlg} onOpenChange={(v) => !v && setConfirmDlg(null)}>
        <DialogContent data-testid="confirm-fy-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirmDlg?.type === "close" ? "Close Financial Year" : "Re-open Financial Year"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Confirm whether to lock or unlock the selected financial year. Closing blocks payroll re-compute and advance disbursement for the period.
            </DialogDescription>
          </DialogHeader>
          {confirmDlg && (
            <div className="space-y-2 text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded p-2">
                <b>{confirmDlg.fy.label}</b><br />
                {fmtDate(confirmDlg.fy.start_date)} → {fmtDate(confirmDlg.fy.end_date)}
              </div>
              {confirmDlg.type === "close" ? (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  <Lock className="h-3 w-3 inline mr-1" />
                  This will <b>lock</b> the FY. Payroll re-compute and advance disbursement in this period will be blocked.
                  Existing records remain viewable. You can re-open later.
                </div>
              ) : (
                <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                  <Unlock className="h-3 w-3 inline mr-1" />
                  This will <b>unlock</b> the FY. Payroll re-compute and advance disbursement in this period will be allowed again.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDlg(null)}>Cancel</Button>
            <Button
              onClick={submitConfirm}
              disabled={busy}
              className={confirmDlg?.type === "close"
                ? "bg-amber-600 hover:bg-amber-700 text-white"
                : "bg-emerald-600 hover:bg-emerald-700 text-white"}
              data-testid="confirm-fy-action-btn"
            >
              {busy ? "Working…" : confirmDlg?.type === "close" ? "Yes, Close FY" : "Yes, Re-open FY"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
